'use client';

import React, { useState, useEffect, useRef } from 'react';

interface LanguageData {
  id: number;
  lang: string;
  langCode: string;
  query: string;
  color: string;
  bgColor: string;
  borderColor: string;
  intentLabel: string;
  answerLabel: string;
  categories: { name: string; growth: string; bar: number }[];
  recommendation: string;
  opportunity: string;
}

const QUERY_CARDS: LanguageData[] = [
  {
    id: 0,
    lang: 'English',
    langCode: 'EN',
    query: 'Which product category is growing fastest?',
    color: '#1F4D3F',
    bgColor: 'rgba(31,77,63,0.06)',
    borderColor: 'rgba(31,77,63,0.18)',
    intentLabel: 'Fastest Growing Product Category',
    answerLabel: 'Fastest Growing Categories',
    categories: [
      { name: 'Snacks', growth: '+18%', bar: 90 },
      { name: 'Dairy', growth: '+14%', bar: 70 },
      { name: 'Staples', growth: '+9%', bar: 45 },
    ],
    recommendation: 'Increase inventory depth in Snacks and Dairy before festive demand rises.',
    opportunity: '₹4.2L Additional Revenue',
  },
  {
    id: 1,
    lang: 'Hinglish',
    langCode: 'HI-EN',
    query: 'Bhai konsa category sabse fast grow kar rahi hai?',
    color: '#2D6A5A',
    bgColor: 'rgba(45,106,90,0.06)',
    borderColor: 'rgba(45,106,90,0.18)',
    intentLabel: 'Sabse Fast Growing Product Category',
    answerLabel: 'Top Growing Categories',
    categories: [
      { name: 'Snacks', growth: '+18%', bar: 90 },
      { name: 'Dairy', growth: '+14%', bar: 70 },
      { name: 'Staples', growth: '+9%', bar: 45 },
    ],
    recommendation: 'Snacks aur Dairy ka inventory badhao — festive season se pehle demand spike hogi.',
    opportunity: '₹4.2L Additional Revenue',
  },
  {
    id: 2,
    lang: 'Hindi',
    langCode: 'HI',
    query: 'Kaunsi category sabse tezi se grow kar rahi hai?',
    color: '#3A7D6B',
    bgColor: 'rgba(58,125,107,0.06)',
    borderColor: 'rgba(58,125,107,0.18)',
    intentLabel: 'Sabse Tezi Se Badhne Wali Category',
    answerLabel: 'Tezi Se Badhti Categories',
    categories: [
      { name: 'Snacks', growth: '+18%', bar: 90 },
      { name: 'Dairy', growth: '+14%', bar: 70 },
      { name: 'Staples', growth: '+9%', bar: 45 },
    ],
    recommendation: 'Snacks aur Dairy ka stock tyoharon se pehle badhayein — maang mein tezi aane ki sambhavna hai.',
    opportunity: '₹4.2L Atirikt Aay',
  },
  {
    id: 3,
    lang: 'Gujarati',
    langCode: 'GU',
    query: 'Kai category ni demand sauthi vadhare vadhi rahi chhe?',
    color: '#1F4D3F',
    bgColor: 'rgba(31,77,63,0.06)',
    borderColor: 'rgba(31,77,63,0.18)',
    intentLabel: 'Sauthi Vadhare Vadhti Product Category',
    answerLabel: 'Vadhti Categories',
    categories: [
      { name: 'Snacks', growth: '+18%', bar: 90 },
      { name: 'Dairy', growth: '+14%', bar: 70 },
      { name: 'Staples', growth: '+9%', bar: 45 },
    ],
    recommendation: 'Snacks ane Dairy no stock tહevaro pehla vadharo — maang vadhvani shakyata chhe.',
    opportunity: '₹4.2L Vadharani Aavak',
  },
];

type Step = 'intent' | 'sources' | 'answer' | 'action' | 'impact';

const STEP_SEQUENCE: Step[] = ['intent', 'sources', 'answer', 'action', 'impact'];
const STEP_DURATIONS: Record<Step, number> = {
  intent: 800,
  sources: 1000,
  answer: 900,
  action: 800,
  impact: 1100,
};

const DATA_SOURCES = [
  'Sales Data',
  'Inventory Trends',
  'Customer Purchases',
  'Purchase Orders',
];

export default function LanguageLayer() {
  const [activeCard, setActiveCard] = useState(0);
  const [displayCard, setDisplayCard] = useState(0);
  const [contentVisible, setContentVisible] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [sourcesVisible, setSourcesVisible] = useState<number[]>([]);
  const [categoriesVisible, setCategoriesVisible] = useState<number[]>([]);
  const [showAction, setShowAction] = useState(false);
  const [showImpact, setShowImpact] = useState(false);
  const [mounted, setMounted] = useState(false);
  const cycleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stepTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const clearStepTimers = () => {
    stepTimers.current.forEach(t => clearTimeout(t));
    stepTimers.current = [];
  };

  const runStepSequence = () => {
    setStepIndex(0);
    setSourcesVisible([]);
    setCategoriesVisible([]);
    setShowAction(false);
    setShowImpact(false);

    let delay = 0;
    STEP_SEQUENCE.forEach((step, idx) => {
      const t = setTimeout(() => {
        setStepIndex(idx);
        if (step === 'sources') {
          DATA_SOURCES.forEach((_, si) => {
            const st = setTimeout(() => {
              setSourcesVisible(prev => [...prev, si]);
            }, si * 200);
            stepTimers.current.push(st);
          });
        }
        if (step === 'answer') {
          [0, 1, 2].forEach(ci => {
            const ct = setTimeout(() => {
              setCategoriesVisible(prev => [...prev, ci]);
            }, ci * 230);
            stepTimers.current.push(ct);
          });
        }
        if (step === 'action') setShowAction(true);
        if (step === 'impact') setShowImpact(true);
      }, delay);
      stepTimers.current.push(t);
      delay += STEP_DURATIONS[step];
    });
  };

  const switchToCard = (idx: number) => {
    if (idx === activeCard) return;
    clearStepTimers();
    setActiveCard(idx);
    // Fade out → swap content → fade in
    setContentVisible(false);
    const t1 = setTimeout(() => {
      setDisplayCard(idx);
      setStepIndex(0);
      setSourcesVisible([]);
      setCategoriesVisible([]);
      setShowAction(false);
      setShowImpact(false);
      setContentVisible(true);
      runStepSequence();
    }, 280);
    stepTimers.current.push(t1);
  };

  useEffect(() => {
    if (!mounted) return;
    setDisplayCard(0);
    runStepSequence();

    cycleRef.current = setInterval(() => {
      setActiveCard(prev => {
        const next = (prev + 1) % QUERY_CARDS.length;
        clearStepTimers();
        setContentVisible(false);
        const t = setTimeout(() => {
          setDisplayCard(next);
          setStepIndex(0);
          setSourcesVisible([]);
          setCategoriesVisible([]);
          setShowAction(false);
          setShowImpact(false);
          setContentVisible(true);
          runStepSequence();
        }, 280);
        stepTimers.current.push(t);
        return next;
      });
    }, 6000);

    return () => {
      if (cycleRef.current) clearInterval(cycleRef.current);
      clearStepTimers();
    };
  }, [mounted]);

  const handleCardClick = (idx: number) => {
    if (cycleRef.current) clearInterval(cycleRef.current);
    switchToCard(idx);
    cycleRef.current = setInterval(() => {
      setActiveCard(prev => {
        const next = (prev + 1) % QUERY_CARDS.length;
        clearStepTimers();
        setContentVisible(false);
        const t = setTimeout(() => {
          setDisplayCard(next);
          setStepIndex(0);
          setSourcesVisible([]);
          setCategoriesVisible([]);
          setShowAction(false);
          setShowImpact(false);
          setContentVisible(true);
          runStepSequence();
        }, 280);
        stepTimers.current.push(t);
        return next;
      });
    }, 6000);
  };

  const card = QUERY_CARDS[displayCard];
  const stepsReached = stepIndex;

  return (
    <section
      id="language"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #FBFAF6 0%, #F0EEF8 40%, #FBFAF6 100%)' }}
      aria-label="VINA understands your intent"
    >
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/2 right-0 -translate-y-1/2 w-[700px] h-[500px]"
          style={{ background: 'radial-gradient(ellipse, rgba(31,77,63,0.07) 0%, transparent 70%)', filter: 'blur(80px)' }}
        />
        <div
          className="absolute top-1/4 left-0 w-[500px] h-[400px]"
          style={{ background: 'radial-gradient(ellipse, rgba(58,125,107,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <p
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              letterSpacing: '0.1em',
              color: '#7B8F88',
              textTransform: 'uppercase',
              marginBottom: '16px',
            }}
          >
            Language Intelligence
          </p>
          <h2
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              letterSpacing: '-0.03em',
              color: '#0D2620',
              lineHeight: 1.1,
              marginBottom: '16px',
            }}
          >
            Ask naturally.
            <br />
            <span style={{ color: '#1F4D3F' }}>VINA understands what you mean.</span>
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '1.05rem',
              color: '#3D5550',
              maxWidth: '560px',
              margin: '0 auto',
              lineHeight: 1.7,
            }}
          >
            Whether you ask in English, Hinglish, or your regional language, VINA understands the business intent behind your question and delivers the right answer.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* LEFT — Floating query cards */}
          <div className="flex flex-col gap-4 relative">
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '10px',
                letterSpacing: '0.08em',
                color: '#9AADA7',
                textTransform: 'uppercase',
                marginBottom: '4px',
              }}
            >
              Different ways to ask the same question
            </p>

            {QUERY_CARDS.map((c, idx) => {
              const isActive = activeCard === idx;
              return (
                <button
                  key={c.id}
                  onClick={() => handleCardClick(idx)}
                  className="w-full text-left rounded-2xl p-5 relative"
                  style={{
                    background: isActive ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.55)',
                    backdropFilter: 'blur(16px)',
                    border: isActive
                      ? `1.5px solid ${c.borderColor}`
                      : '1px solid rgba(221,231,227,0.7)',
                    boxShadow: isActive
                      ? `0 12px 40px rgba(31,77,63,0.12), 0 2px 8px rgba(31,77,63,0.06)`
                      : '0 2px 8px rgba(31,77,63,0.04)',
                    transform: isActive ? 'translateY(-3px) scale(1.01)' : 'translateY(0) scale(1)',
                    transition: 'all 0.45s cubic-bezier(0.22,1,0.36,1)',
                    cursor: 'pointer',
                  }}
                  aria-pressed={isActive}
                  aria-label={`Query in ${c.lang}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{
                          background: c.color,
                          boxShadow: isActive ? `0 0 8px ${c.color}60` : 'none',
                          transition: 'box-shadow 0.3s ease',
                        }}
                      />
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '10px',
                          color: isActive ? c.color : '#9AADA7',
                          letterSpacing: '0.07em',
                          textTransform: 'uppercase',
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {c.lang}
                      </span>
                    </div>
                    {isActive && (
                      <div
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                        style={{
                          background: 'rgba(31,77,63,0.08)',
                          border: '1px solid rgba(31,77,63,0.12)',
                          animation: 'fadeIn 0.3s ease both',
                        }}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: '#4F9B7A', animation: 'pulse 1.5s ease-in-out infinite' }}
                        />
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '9px',
                            color: '#1F4D3F',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Active
                        </span>
                      </div>
                    )}
                  </div>
                  <p
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: '15px',
                      fontWeight: isActive ? 500 : 400,
                      color: isActive ? '#0D2620' : '#5A7068',
                      lineHeight: 1.5,
                      letterSpacing: '-0.01em',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {c.query}
                  </p>

                  {/* Connection line indicator */}
                  {isActive && (
                    <div
                      className="absolute right-0 top-1/2 -translate-y-1/2 hidden lg:flex items-center"
                      style={{ right: '-32px' }}
                      aria-hidden="true"
                    >
                      <div
                        style={{
                          width: '28px',
                          height: '1.5px',
                          background: `linear-gradient(90deg, ${c.color}60, ${c.color})`,
                          animation: 'expandLine 0.4s ease both',
                        }}
                      />
                      <div
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: c.color,
                          boxShadow: `0 0 8px ${c.color}80`,
                          animation: 'pulse 1.5s ease-in-out infinite',
                        }}
                      />
                    </div>
                  )}
                </button>
              );
            })}

            {/* Progress dots */}
            <div className="flex items-center gap-2 mt-2 pl-1">
              {QUERY_CARDS.map((c, idx) => (
                <div
                  key={c.id}
                  style={{
                    width: activeCard === idx ? '20px' : '6px',
                    height: '6px',
                    borderRadius: '3px',
                    background: activeCard === idx ? c.color : 'rgba(31,77,63,0.18)',
                    transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)',
                  }}
                />
              ))}
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '9px',
                  color: '#9AADA7',
                  letterSpacing: '0.04em',
                  marginLeft: '6px',
                }}
              >
                Changes every 6s
              </span>
            </div>
          </div>

          {/* RIGHT — Intelligence card */}
          <div className="relative">
            <div
              className="rounded-3xl overflow-hidden"
              style={{
                background: 'rgba(255,255,255,0.88)',
                backdropFilter: 'blur(24px)',
                border: '1.5px solid rgba(221,231,227,0.9)',
                boxShadow: '0 24px 64px rgba(31,77,63,0.1), 0 4px 16px rgba(31,77,63,0.06)',
              }}
            >
              {/* Card header */}
              <div
                className="px-6 py-4 flex items-center justify-between"
                style={{ borderBottom: '1px solid rgba(221,231,227,0.6)' }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: '#4F9B7A', animation: 'pulse 2s ease-in-out infinite' }}
                  />
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '10px',
                      color: '#7B8F88',
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                    }}
                  >
                    VINA · Intelligence
                  </span>
                </div>
                {/* Language badge */}
                <div
                  className="px-2.5 py-1 rounded-full"
                  style={{
                    background: 'rgba(31,77,63,0.07)',
                    border: '1px solid rgba(31,77,63,0.14)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '9px',
                      color: '#1F4D3F',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {card.langCode}
                  </span>
                </div>
              </div>

              {/* Animated content wrapper */}
              <div
                className="p-6 flex flex-col gap-5"
                style={{
                  opacity: contentVisible ? 1 : 0,
                  transform: contentVisible ? 'translateY(0)' : 'translateY(6px)',
                  transition: 'opacity 0.28s ease, transform 0.28s ease',
                }}
              >

                {/* STEP 1 — Intent Detected */}
                <div
                  style={{
                    opacity: stepsReached >= 0 ? 1 : 0,
                    transform: stepsReached >= 0 ? 'translateY(0)' : 'translateY(8px)',
                    transition: 'opacity 0.4s ease, transform 0.4s ease',
                  }}
                >
                  <div
                    className="flex items-center gap-3 px-4 py-3 rounded-2xl"
                    style={{
                      background: 'rgba(79,155,122,0.08)',
                      border: '1px solid rgba(79,155,122,0.2)',
                    }}
                  >
                    <div
                      className="flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0"
                      style={{ background: 'rgba(79,155,122,0.15)' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M2.5 7L5.5 10L11.5 4" stroke="#4F9B7A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <p
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '9px',
                          color: '#4F9B7A',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          marginBottom: '2px',
                        }}
                      >
                        Intent Detected
                      </p>
                      <p
                        style={{
                          fontFamily: 'var(--font-sans)',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#0D2620',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {card.intentLabel}
                      </p>
                    </div>
                  </div>
                </div>

                {/* STEP 2 — Data Sources */}
                <div
                  style={{
                    opacity: stepsReached >= 1 ? 1 : 0,
                    transform: stepsReached >= 1 ? 'translateY(0)' : 'translateY(8px)',
                    transition: 'opacity 0.4s ease 0.1s, transform 0.4s ease 0.1s',
                  }}
                >
                  <p
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '9px',
                      color: '#9AADA7',
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      marginBottom: '8px',
                    }}
                  >
                    Analyzing Data Sources
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {DATA_SOURCES.map((source, idx) => (
                      <div
                        key={source}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                        style={{
                          background: sourcesVisible.includes(idx) ? 'rgba(31,77,63,0.07)' : 'rgba(221,231,227,0.4)',
                          border: sourcesVisible.includes(idx)
                            ? '1px solid rgba(31,77,63,0.18)'
                            : '1px solid rgba(221,231,227,0.6)',
                          opacity: sourcesVisible.includes(idx) ? 1 : 0.4,
                          transition: 'all 0.35s ease',
                        }}
                      >
                        {sourcesVisible.includes(idx) && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#4F9B7A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '10px',
                            color: sourcesVisible.includes(idx) ? '#1F4D3F' : '#9AADA7',
                            letterSpacing: '0.03em',
                            transition: 'color 0.3s ease',
                          }}
                        >
                          {source}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* STEP 3 — Business Answer */}
                <div
                  style={{
                    opacity: stepsReached >= 2 ? 1 : 0,
                    transform: stepsReached >= 2 ? 'translateY(0)' : 'translateY(8px)',
                    transition: 'opacity 0.4s ease 0.1s, transform 0.4s ease 0.1s',
                  }}
                >
                  <p
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '9px',
                      color: '#9AADA7',
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      marginBottom: '10px',
                    }}
                  >
                    {card.answerLabel}
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {card.categories.map((cat, idx) => (
                      <div key={cat.name} className="flex items-center gap-3">
                        <span
                          style={{
                            fontFamily: 'var(--font-sans)',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#0D2620',
                            width: '60px',
                            flexShrink: 0,
                          }}
                        >
                          {cat.name}
                        </span>
                        <div
                          className="flex-1 rounded-full overflow-hidden"
                          style={{ height: '6px', background: 'rgba(221,231,227,0.6)' }}
                        >
                          <div
                            style={{
                              height: '100%',
                              width: categoriesVisible.includes(idx) ? `${cat.bar}%` : '0%',
                              background: 'linear-gradient(90deg, #4F9B7A, #1F4D3F)',
                              borderRadius: '3px',
                              transition: `width 0.7s cubic-bezier(0.22,1,0.36,1) ${idx * 0.15}s`,
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#4F9B7A',
                            width: '40px',
                            textAlign: 'right',
                            flexShrink: 0,
                          }}
                        >
                          {cat.growth}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* STEP 4 — Recommended Action */}
                {showAction && (
                  <div
                    className="rounded-2xl p-4"
                    style={{
                      background: 'rgba(31,77,63,0.05)',
                      border: '1px solid rgba(31,77,63,0.12)',
                      animation: 'fadeSlideUp 0.4s ease both',
                    }}
                  >
                    <p
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '9px',
                        color: '#9AADA7',
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        marginBottom: '6px',
                      }}
                    >
                      Recommended Action
                    </p>
                    <p
                      style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#0D2620',
                        lineHeight: 1.5,
                      }}
                    >
                      {card.recommendation}
                    </p>
                  </div>
                )}

                {/* STEP 5 — Impact Badge */}
                {showImpact && (
                  <div
                    className="flex items-center justify-between rounded-2xl px-4 py-3"
                    style={{
                      background: 'linear-gradient(135deg, rgba(79,155,122,0.1) 0%, rgba(31,77,63,0.08) 100%)',
                      border: '1px solid rgba(79,155,122,0.25)',
                      animation: 'fadeSlideUp 0.4s ease both',
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '9px',
                          color: '#4F9B7A',
                          letterSpacing: '0.07em',
                          textTransform: 'uppercase',
                          marginBottom: '3px',
                        }}
                      >
                        Potential Opportunity
                      </p>
                      <p
                        style={{
                          fontFamily: 'var(--font-sans)',
                          fontSize: '20px',
                          fontWeight: 700,
                          color: '#0D2620',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {card.opportunity}
                      </p>
                    </div>
                    <div
                      className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0"
                      style={{ background: 'rgba(79,155,122,0.15)' }}
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                        <path d="M9 3V15M9 3L5 7M9 3L13 7" stroke="#4F9B7A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Microcopy */}
                <p
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '10px',
                    color: '#9AADA7',
                    letterSpacing: '0.04em',
                    textAlign: 'center',
                    paddingTop: '4px',
                    opacity: stepsReached >= 2 ? 1 : 0,
                    transition: 'opacity 0.4s ease',
                  }}
                >
                  Ask naturally. Get answers the way your team works.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes expandLine {
          from { width: 0; opacity: 0; }
          to { width: 28px; opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </section>
  );
}
